/**
 * `/trash` — what you deleted, and getting it back.
 *
 * Scoped to the caller: what they own, or what they deleted. It is not an
 * administrator's view of everybody's deletions, and saying so on screen saves
 * somebody concluding the system lost their colleague's file.
 *
 * Restoring can rename. If something took the name in the meantime, the server
 * restores under a different one and says so in `renamedTo` — which is worth
 * surfacing, because a file that comes back as "report (2).pdf" is otherwise a
 * small mystery.
 */
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import FolderIcon from '@mui/icons-material/Folder';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrash';
import Alert from '@mui/material/Alert';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { AsyncContent } from '../../components/AsyncContent';
import { EntityTable } from '../../components/EntityTable';
import { ErrorState } from '../../components/ErrorState';
import { PageHeader } from '../../components/PageHeader';
import { Timestamp } from '../../components/Timestamp';
import { useAsync } from '../../hooks/useAsync';
import { useListQuery } from '../../hooks/useListQuery';
import { useSubmit } from '../../hooks/useSubmit';
import { listLibraries, listTrash, restoreFromTrash } from '../../api/resources';

export default function TrashPage() {
  const { t } = useTranslation('files');
  const [library, setLibrary] = useState('');
  const [renamed, setRenamed] = useState<string | null>(null);

  const libraries = useAsync(() => listLibraries(), []);
  // The library filter changes what is listed, so it resets the page.
  const list = useListQuery({ defaultSort: 'deletedAt', defaultOrder: 'desc', resetKey: library });
  const trash = useAsync(
    () => listTrash(library || undefined, list.query),
    [library, list.key],
  );

  const restore = useSubmit(async (id: string) => {
    const result = await restoreFromTrash(id);
    setRenamed(result.renamedTo);
    trash.reload();
    return result;
  });

  return (
    <>
      <PageHeader
        title={t('trash.title')}
        description={t('trash.description')}
        actions={
          <TextField
            select
            size="small"
            label={t('trash.library')}
            value={library}
            onChange={(event) => setLibrary(event.target.value)}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">{t('trash.allLibraries')}</MenuItem>
            {(libraries.data?.libraries ?? []).map((entry) => (
              <MenuItem key={entry.key} value={entry.key}>
                {entry.name}
              </MenuItem>
            ))}
          </TextField>
        }
      />

      {renamed && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setRenamed(null)}>
          <Trans
            i18nKey="trash.renamed"
            ns="files"
            values={{ name: renamed }}
            components={{ strong: <strong /> }}
          />
        </Alert>
      )}
      {restore.error && <ErrorState error={restore.error} />}

      <AsyncContent state={trash}>
        {({ resources, page }) => (
          <EntityTable
            rows={resources}
            rowKey={(resource) => resource.id}
            list={list}
            page={page}
            actionsWidth={80}
            empty={{
              icon: <DeleteOutlinedIcon fontSize="inherit" />,
              title: t('trash.emptyTitle'),
              description: t('trash.emptyDescription'),
            }}
            columns={[
              {
                field: 'name',
                label: t('trash.column.name'),
                sortable: true,
                render: (resource) => (
                  <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                    {resource.kind === 'folder' ? (
                      <FolderIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                    ) : (
                      <InsertDriveFileOutlinedIcon
                        fontSize="small"
                        sx={{ color: 'text.secondary' }}
                      />
                    )}
                    <Typography variant="body2">{resource.name}</Typography>
                  </Stack>
                ),
              },
              {
                field: 'library',
                label: t('trash.column.library'),
                sortable: true,
                width: 160,
                render: (resource) => (
                  <Typography variant="caption" color="text.secondary">
                    {resource.library}
                  </Typography>
                ),
              },
              {
                field: 'deletedAt',
                label: t('trash.column.deleted'),
                sortable: true,
                width: 160,
                render: (resource) => (
                  <Typography variant="caption" color="text.secondary">
                    <Timestamp value={resource.deletedAt} />
                  </Typography>
                ),
              },
            ]}
            actions={(resource) => [
              {
                key: 'restore',
                label: t('trash.restore'),
                icon: <RestoreFromTrashIcon fontSize="small" />,
                onClick: () => void restore.submit(resource.id),
                disabled: restore.pending,
              },
            ]}
          />
        )}
      </AsyncContent>
    </>
  );
}
